// InspectionFormModal.jsx
import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Badge from './Badge';
import LoadingSpinner from './LoadingSpinner';

const InspectionFormModal = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  technicians,
  handleDelete,
  isEditing,
  disabled,
  loading
}) => {
  const [jobSiteQuery, setJobSiteQuery] = useState('');
  const [jobSiteResults, setJobSiteResults] = useState([]);
  const [isJobSiteDropdownVisible, setIsJobSiteDropdownVisible] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };


    useEffect(() => {
    // Whenever the modal closes, reset the child’s internal search states
    if (!isOpen) {
      setJobSiteQuery('');
      setJobSiteResults([]);
      setIsJobSiteDropdownVisible(false);
    }
  }, [isOpen]);
  
  const handleJobSiteInputChange = (e) => {
    const value = e.target.value;
    setJobSiteQuery(value);
    
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    if (value.length >= 3) {
      const timeout = setTimeout(async () => {
        // Fetch job sites from the backend
        try {
          const response = await axios.get(`${apiUrl}/contracts_crud.php`, {
            params: { getJobSites: value },
          });
          const results = Array.isArray(response.data) ? response.data : [];
          setJobSiteResults(results);
          setIsJobSiteDropdownVisible(true);
        } catch (error) {
          // Handle errors
          console.error('Error fetching job sites:', error);
        }
      }, 500); // Delay of 500ms

      setDebounceTimeout(timeout);
    } else {
      setJobSiteResults([]);
      setIsJobSiteDropdownVisible(false);
    }
  };

  const handleJobSiteSelect = (jobSite) => {
  //  let due = jobSite.next_due_date ? new Date(jobSite.next_due_date).toISOString().split('T')[0] : '';
    // let date = new Date().toISOString().split('T')[0]
    // if (jobSite.next_due_date < date) {
    //   toast.error('This job site is overdue for inspection', {
    //     position: 'top-center',
    //     autoClose: 5000,
    //     hideProgressBar: false,
    //     closeOnClick: true,
    //     pauseOnHover: true,
    //     draggable: true,
    //   });
    //   return;
    // }
    
    setJobSiteQuery(jobSite.job_site); // Display the selected job site's address
    setIsJobSiteDropdownVisible(false);
    setFormData((prevData) => ({
      ...prevData,
      maintenance_id: jobSite.maintenance_id,
      job_site_name: jobSite.job_site,
      next_due_date: jobSite.next_due_date,
    }));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-20"
        onClose={onClose}
      >
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        {/* Modal content */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white/90 backdrop-blur-md shadow-xl ring-1 ring-gray-200">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-green-300 px-6 py-4 bg-green-500">
                  <Dialog.Title className="text-xl font-semibold text-gray-900">
                    {formData.id ? 'Edit Inspection' : 'New Inspection'}
                  </Dialog.Title>

                  {/* Optional status pill (show if you track status) */}
                  {formData?.status && (
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${
                        String(formData.status).toLowerCase() === 'completed'
                          ? 'bg-green-50 text-green-700 ring-green-600/20'
                          : 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'
                      }`}
                    >
                      {formData.status}
                    </span>
                  )}
                </div>

                {/* Body (form) */}
                <form id="inspection-form" onSubmit={onSubmit} className="px-6 py-5 space-y-6">

                  {/* Tentative flag */}
                  <div>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="tentative"
                        checked={!!formData.tentative}
                        onChange={handleInputChange}
                        className="h-4 w-4 rounded border-red-400 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm font-semibold text-red-600">Tentative</span>
                    </label>
                  </div>

                  {/* Status Description (visible only if Tentative) */}
                  {Boolean(formData.tentative) && (
                    <div>
                      <label className="block text-sm font-medium text-red-700">
                        Status Description (required when Tentative)
                      </label>
                      <textarea
                        name="status_description"
                        value={formData.status_description || ''}
                        onChange={handleInputChange}
                        rows={3}
                        className="mt-1 block w-full rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-gray-900 focus:border-red-500 focus:ring-red-500"
                        placeholder="Explain why this inspection is tentative…"
                        required
                      />
                    </div>
                  )}

                  {/* Job Site search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Job Site</label>
                    <div className="relative">
                      <input
                        type="text"
                        name="job_site"
                        value={
                          jobSiteQuery
                            ? jobSiteQuery
                            : formData.id
                              ? `${formData.job_site_address || ''}${formData.job_site_city ? ', ' + formData.job_site_city : ''}${formData.job_site_state ? ', ' + formData.job_site_state : ''}${formData.job_site_zip ? ' ' + formData.job_site_zip : ''}`
                              : ''
                        }
                        onChange={handleJobSiteInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />

                      {/* Dropdown List */}
                      {isJobSiteDropdownVisible && jobSiteResults.length > 0 && (
                        <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-auto divide-y divide-gray-100 rounded-lg border bg-white shadow-lg">
                          {jobSiteResults.map((jobSite) => {
                            const liBgClass =
                              jobSite.status === 0
                                ? 'bg-red-50 hover:bg-red-100 text-red-700'
                                : 'hover:bg-indigo-50';
                            return (
                              <li
                                key={jobSite.maintenance_id}
                                className={`px-4 py-2 cursor-pointer ${liBgClass}`}
                                onClick={() => handleJobSiteSelect(jobSite)}
                              >
                                {jobSite.status === 0 && (
                                  <div className="text-red-700 p-1 text-xs font-semibold">
                                    ********** Inactive Contract **********
                                  </div>
                                )}
                                {jobSite.contract_hold_amount && (
                                  <div className="text-red-700 p-1 text-xs font-semibold">
                                    ********** Contract On Hold **********
                                  </div>
                                )}
                                <div className="text-sm font-medium text-gray-900">
                                  <span className="inline-block rounded bg-yellow-50 px-1.5 py-0.5">
                                    ID:{jobSite.id} | {jobSite.customer_name}
                                  </span>{' '}
                                  | {jobSite.contract_type} | {jobSite.system_type}
                                </div>
                                <div className="font-medium text-gray-800">{jobSite.job_site}</div>
                                <div className="text-xs text-green-600">
                                  Next Inspection Due: {jobSite.next_due_date}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Schedule grid */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Schedule Date</label>
                      <input
                        type="date"
                        name="scheduled_due_date"
                        value={formData.scheduled_due_date}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Time In</label>
                      <input
                        type="time"
                        name="schedule_time_in"
                        value={formData.schedule_time_in}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">End Date</label>
                      <input
                        type="date"
                        name="schedule_date_to"
                        value={
                          formData.schedule_date_to
                            ? formData.schedule_date_to
                            : formData.scheduled_due_date || new Date().toISOString().split('T')[0]
                        }
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Time Out</label>
                      <input
                        type="time"
                        name="schedule_time_out"
                        value={formData.schedule_time_out}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Techs */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Lead Technician</label>
                      <select
                        name="lead_tech_id"
                        value={formData.lead_tech_id || ''}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="">Select Technician</option>
                        {technicians.map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.first_name} {tech.last_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Additional Technician</label>
                      <select
                        name="additional_tech_id"
                        value={formData.additional_tech_id}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="">Additional Tech</option>
                        {technicians.map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.first_name} {tech.last_name}
                          </option>
                        ))}
                      </select>

                      {/* Same-tech warning */}
                      {formData.lead_tech_id &&
                        formData.additional_tech_id &&
                        parseInt(formData.lead_tech_id) === parseInt(formData.additional_tech_id) && (
                          <p className="mt-1 text-xs font-medium text-red-600">
                            Lead Tech and Additional Tech cannot be the same person.
                          </p>
                        )}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Inspection Notes</label>
                    <textarea
                      name="inspection_notes"
                      value={formData.inspection_notes}
                      onChange={handleInputChange}
                      rows={4}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  {loading ? <LoadingSpinner />  : (
                  <>
                  {/* Footer (inside form so submit works) */}
                  <div className="mt-2 flex items-center justify-end gap-2 border-t border-gray-300  -mx-6 px-6 pt-4">
                    {isEditing && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const idForDelete = formData?.id || formData?.inspection_id;
                          handleDelete?.(idForDelete);
                        }}
                        className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Delete
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {formData.id ? 'Update' : 'Create'}
                    </button>
                  </div>                  
                  
                  </>                    
                  )}
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default InspectionFormModal;
